import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Routes, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import {
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { BrowserCacheLocation, BrowserUtils, InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { BnNgIdleService } from 'bn-ng-idle';
import { APP_DATE_FORMATS } from '@shared/constants';
import { HeaderApimKeyInterceptor } from '@shared/interceptors';
import { ConfigService } from '@shared/services';
import { environment } from '@environments/environment';
import { CurrentUserGuard } from '@shared/guards';
import { LoggedOutComponent } from './shared/components/logged-out/logged-out.component';

// MSAL Factory Functions
export const MSALInstanceFactory = (configService: ConfigService): IPublicClientApplication => {
    return new PublicClientApplication({
        auth: {
            clientId: configService.appConfig.clientId,
            authority: configService.appConfig.authority,
            redirectUri: '/',
            postLogoutRedirectUri: '/',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
        },
    });
};

export const MSALInterceptorConfigFactory = (configService: ConfigService): MsalInterceptorConfiguration => {
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
            [environment.graphEndpoint, ['user.read']],
            [configService.appConfig.apiEndpoint, [configService.appConfig.apiAuthenticationScope]],
        ]),
    } as MsalInterceptorConfiguration;
};

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: ['user.read'] },
    } as MsalGuardConfiguration;
}

// App Initializer
export function initializeApp(configService: ConfigService) {
    return () => configService.loadConfig();
}

// Routes
const routes: Routes = [
    {
        path: '',
        loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'home',
        redirectTo: '',
    },
    {
        path: 'new-report',
        loadChildren: () => import('./modules/new-report/new-report.module').then(m => m.NewReportModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'report-status',
        loadChildren: () => import('./modules/report-status/report-status.module').then(m => m.ReportStatusModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'my-reports',
        loadChildren: () => import('./modules/my-reports/my-reports.module').then(m => m.MyReportsModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'predefined-reports',
        loadChildren: () => import('./modules/predefined-reports/predefined-reports.module').then(m => m.PredefinedReportsModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'user-management',
        loadChildren: () => import('./modules/user-management/user-management.module').then(m => m.UserManagementModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard',
        loadChildren: () => import('./modules/metadata-dashboard/metadata-dashboard.module').then(m => m.MetadataDashboardModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'adhoc-reports-admin',
        loadChildren: () => import('./modules/adhoc-reports-admin/adhoc-reports-admin.module').then(m => m.AdhocReportsAdminModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'status-dashboard',
        loadChildren: () => import('./modules/status-dashboard/status-dashboard.module').then(m => m.StatusDashboardModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'system-configuration',
        loadChildren: () => import('./modules/system-configuration/system-configuration.module').then(m => m.SystemConfigurationModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'new-template/wizard',
        loadChildren: () => import('./modules/template-wizard/template-wizard.module').then(m => m.TemplateWizardModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard/table-relations',
        loadChildren: () => import('./modules/table-relations/table-relations.module').then(m => m.TableRelationsModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard/fact-details',
        loadChildren: () => import('./modules/fact-details/fact-details.module').then(m => m.FactDetailsModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard/sql-views',
        loadChildren: () => import('./modules/sql-view/sql-views.module').then(m => m.SqlViewsModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard/sections',
        loadChildren: () => import('./modules/sections/sections.module').then(m => m.SectionsModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard/nodes-measures',
        loadChildren: () => import('./modules/nodes-measures/nodes-measures.module').then(m => m.NodesMeasuresModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'metadata-dashboard/templates',
        loadChildren: () => import('./modules/templates/templates.module').then(m => m.TemplatesModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard, CurrentUserGuard.canActivate] : [])],
    },
    {
        path: 'error',
        loadChildren: () => import('./modules/error/error.module').then(m => m.ErrorModule),
        canActivate: [...(environment.useAuthorization ? [MsalGuard] : [])],
    },
    {
        path: 'logged-out',
        component: LoggedOutComponent
    },
    {
        path: '**',
        redirectTo: '',
    },
];

export const appConfig: ApplicationConfig = {
    providers: [
        // Zone Change Detection
        provideZoneChangeDetection({ eventCoalescing: true }),

        // Router with MSAL-compatible initial navigation
        provideRouter(
            routes,
            !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
                ? withEnabledBlockingInitialNavigation()
                : undefined as any
        ),

        // Animations
        provideAnimations(),

        // HTTP Client
        provideHttpClient(withInterceptorsFromDi()),

        // App Initializer
        provideAppInitializer(() => {
            const initializerFn = (initializeApp)(inject(ConfigService));
            return initializerFn();
        }),

        // Material Date Configuration
        provideNativeDateAdapter(),
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'en-GB',
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: APP_DATE_FORMATS,
        },

        // MSAL Configuration
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
            deps: [ConfigService],
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
            deps: [ConfigService],
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,

        // HTTP Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderApimKeyInterceptor,
            multi: true,
        },

        // Charts
        provideCharts(withDefaultRegisterables()),

        // Highlight.js
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
                languages: {
                    sql: () => import('highlight.js/lib/languages/sql'),
                },
            },
        },

        // Idle Service
        BnNgIdleService,
    ]
};
